/**
 * 「誰に・何を送るか」のオブジェクトタイプごとの上書き。
 *
 * 接続設定 → クライアントID → 送信設定 の画面下部に置く。
 * 独立したタブは作らない（同じ目的の設定が2箇所にあると値の出自を追えなくなる）。
 *
 * 階層は2段に留める:
 *   オブジェクトタイプ（折りたたみ）→ セクション（折りたたまない）
 * 3段にするとどこを開閉しているか分からなくなる。
 *
 * 保存はオブジェクトタイプ単位のバッチ1リクエスト。部分保存は起こらない。
 */
import { useState, useEffect } from "react";
import {
  api,
  type ClientSendOverrideSection,
  type ClientSendAppliedState,
  type StorageOverrideValue,
  type Pipeline,
  type PipelineStage,
  type TemplateMapEntry,
  type StampMapEntry,
} from "../api.js";
import { card, labelStyle, inputStyle, selectStyle, btnPrimary, btnSecondary, Badge } from "./ui.js";
import { PropSelect, type HubSpotProp } from "./PropSelect.js";
import StampPositionsForm from "./StampPositionsForm.js";
import {
  OVERRIDE_SECTIONS,
  SECTION_LABELS,
  RECIPIENT_SOURCE_LABELS,
  SHARE_SOURCE_LABELS,
  summarizeBlock,
  describeDefault,
  groupByObjectType,
  initialBlockState,
  buildSavePayload,
  buildClearPayload,
  isInheritedTemplateMissing,
  defaultBlockStateFromMapping,
  buildDefaultMappingFields,
  summarizeDefaultBlock,
  describeStorageDefault,
  storageValueOf,
  overrideInitialValue,
  DEFAULT_SECTIONS,
  type BlockState,
} from "./clientSendOverrideModel.js";
import { mergeOwnedFields, deriveSourceFileProps } from "./objectMappingFieldOwnership.js";

interface ObjectOption {
  key: string;
  label: string;
}

/**
 * デフォルト送信設定の画面では継承が無く、SectionBox の `defaults` は参照されない。
 * 型を満たすためだけのプレースホルダ。
 */
const EMPTY_DEFAULTS: ClientSendAppliedState["defaults"] = {
  recipientSlots: [],
  shareRecipientSlots: [],
  fileSlots: [],
  overflowMode: "attachment",
  recipientSource: "property",
  fixedRecipients: [],
  shareRecipientSource: "property",
  defaultTemplateId: null,
  templateSource: "fixed",
  templateProp: "",
  templateMap: [],
  stampProp: "",
  stampMap: [],
  firstSignerNameProp: "",
  firstSignerEmailProp: "",
  gateProp: "",
  gateOperator: "not_empty",
  gateValues: [],
  overriddenSections: [],
};

/**
 * デフォルト画面では継承が無いため、この既定値は参照されない
 * （格納先セクション自体は描画される）。型を満たすためのプレースホルダ。
 */
const EMPTY_STORAGE_DEFAULTS: ClientSendAppliedState["storageDefaults"] = {
  propStatus: "",
  propDocumentId: "",
  propDraftUrl: "",
  propCompletedUrl: "",
  completedUrlMode: "url_only",
  reuploadProp: "",
  completedFileWriteMode: "overwrite",
  agreementUrlMode: "none",
  agreementReuploadProp: "",
  agreementFileWriteMode: "overwrite",
  overridden: false,
};

// ─────────────────────────────── セクションの編集 ───────────────────────────────

function SectionBox({
  section,
  state,
  defaults,
  templates,
  templatesLoaded,
  properties,
  pipelines,
  pipelineStages,
  storageDefaults,
  allowInherit,
  onChange,
}: {
  section: ClientSendOverrideSection;
  state: BlockState[ClientSendOverrideSection];
  defaults: ClientSendAppliedState["defaults"];
  templates: Array<{ id: string; title: string }>;
  templatesLoaded: boolean;
  properties: HubSpotProp[];
  /** 送信ガードでパイプライン・ステージを選ばせるための一覧（gate セクションのみ使う） */
  pipelines: Pipeline[];
  pipelineStages: PipelineStage[];
  /** 格納先セクションの既定値。継承中の表示と「個別に設定」の初期値に使う */
  storageDefaults: ClientSendAppliedState["storageDefaults"];
  /** デフォルト送信設定の画面では継承元が無いので、継承の2択を出さない */
  allowInherit: boolean;
  onChange: (next: BlockState[ClientSendOverrideSection]) => void;
}) {
  const inherited = state.mode === "inherit";

  /** 「個別に設定」へ切り替えるときは、いま効いている値を初期値にする */
  const toOverride = () => {
    onChange({ mode: "override", value: overrideInitialValue(section, defaults, storageDefaults) });
  };

  const templateMissing =
    section === "template" &&
    inherited &&
    isInheritedTemplateMissing(defaults.defaultTemplateId, templates, templatesLoaded);

  // overflow: hidden を付けてはいけない。中の PropSelect のドロップダウンが
  // 絶対配置なので、枠で切られて選択肢が見えなくなる。
  // 角丸はヘッダー自身に付けて見た目を保つ
  return (
    <div style={{ border: "1px solid #e8e7e3", borderRadius: 10, marginBottom: 12 }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", background: "#fbfaf8", borderBottom: "1px solid #e8e7e3", borderRadius: "10px 10px 0 0", flexWrap: "wrap" }}>
        <span style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{SECTION_LABELS[section]}</span>
        {allowInherit && (inherited
          ? <Badge bg="#F0EFEB" color="#6b6b68">デフォルト設定に従う</Badge>
          : <Badge bg="#E1F5EE" color="#0F6E56">個別に設定</Badge>)}
        {allowInherit && (
        <div style={{ display: "flex", border: "1px solid #e8e7e3", borderRadius: 8, overflow: "hidden" }}>
          {([
            { m: "inherit" as const, label: "デフォルト設定に従う" },
            { m: "override" as const, label: "個別に設定" },
          ]).map((opt) => (
            <button
              key={opt.m}
              onClick={() => {
                if (opt.m === state.mode) return;
                if (opt.m === "inherit") onChange({ mode: "inherit" });
                else toOverride();
              }}
              style={{
                padding: "6px 14px", fontSize: 12, border: "none", cursor: "pointer",
                background: state.mode === opt.m ? "#534AB7" : "white",
                color: state.mode === opt.m ? "white" : "#6b6b68",
                fontWeight: state.mode === opt.m ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        )}
      </header>

      <div style={{ padding: 18, background: inherited ? "#fcfcfb" : "white" }}>
        {inherited ? (
          <>
            <div style={{ fontSize: 13, color: "#9a9a96", padding: "10px 14px", background: "#f7f6f3", borderRadius: 8, border: "1px dashed #ddd", lineHeight: 1.8 }}>
              デフォルト設定に従う — <b style={{ color: "#6b6b68" }}>現在：{section === "storage" ? describeStorageDefault(storageDefaults) : describeDefault(section, defaults)}</b>
              <br />
              デフォルト設定を変更すると、ここも自動で変わります。
            </div>
            {templateMissing && (
              <div style={{ marginTop: 10, padding: "12px 16px", background: "#FAEEDA", borderLeft: "3px solid #BA7517", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#854F0B", lineHeight: 1.8 }}>
                <b>このデフォルト値はこのクライアントIDでは使用できません。</b>
                <br />
                デフォルトのテンプレートがこのクライアントIDのクラウドサインアカウントに存在しません。
                このまま送信するとテンプレートが選ばれていない状態になります。
                「個別に設定」でテンプレートを選び直してください。
              </div>
            )}
          </>
        ) : section === "recipients" ? (
          <RecipientsForm
            value={state.value as any}
            properties={properties}
            onChange={(v) => onChange({ mode: "override", value: v })}
          />
        ) : section === "files" ? (
          <FilesForm
            value={state.value as any}
            properties={properties}
            onChange={(v) => onChange({ mode: "override", value: v })}
          />
        ) : section === "template" ? (
          <TemplateForm
            value={state.value as any}
            templates={templates}
            templatesLoaded={templatesLoaded}
            properties={properties}
            onChange={(v) => onChange({ mode: "override", value: v })}
          />
        ) : section === "gate" ? (
          <GateForm
            value={state.value as any}
            properties={properties}
            pipelineStages={pipelineStages}
            pipelines={pipelines}
            onChange={(v) => onChange({ mode: "override", value: v })}
          />
        ) : section === "storage" ? (
          <StorageForm
            value={state.value as any}
            properties={properties}
            onChange={(v) => onChange({ mode: "override", value: v })}
          />
        ) : (
          <ShareRecipientsForm
            value={state.value as any}
            properties={properties}
            onChange={(v) => onChange({ mode: "override", value: v })}
          />
        )}
      </div>
    </div>
  );
}

/**
 * 宛先スロット / 共有先スロットの編集。
 * 氏名とメールアドレスのプロパティを組で指定する。
 */
/** 宛先・共有先スロット。access_code_prop は宛先だけが使う */
type Slot = { name_prop: string; email_prop: string; access_code_prop?: string };

function SlotEditor({
  label,
  hint,
  slots,
  properties,
  onChange,
  showAccessCode,
}: {
  label: string;
  hint: string;
  slots: Slot[];
  properties: HubSpotProp[];
  onChange: (next: Slot[]) => void;
  /** アクセスコード欄を出すか。共有先には無いので宛先だけ true にする */
  showAccessCode?: boolean;
}) {
  return (
    <>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: 12, color: "#6b6b68", marginBottom: 10, lineHeight: 1.7 }}>{hint}</div>
      {slots.map((slot, i) => (
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#9a9a96", marginBottom: 4 }}>氏名</div>
            <PropSelect
              value={slot.name_prop}
              properties={properties}
              onChange={(v) => {
                const next = [...slots];
                next[i] = { ...slot, name_prop: v };
                onChange(next);
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#9a9a96", marginBottom: 4 }}>メールアドレス</div>
            <PropSelect
              value={slot.email_prop}
              properties={properties}
              onChange={(v) => {
                const next = [...slots];
                next[i] = { ...slot, email_prop: v };
                onChange(next);
              }}
            />
          </div>
          {showAccessCode && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#9a9a96", marginBottom: 4 }}>アクセスコード（任意）</div>
              <PropSelect
                value={slot.access_code_prop ?? ""}
                properties={properties}
                onChange={(v) => {
                  const next = [...slots];
                  // 未選択は「使わない」。空文字ではなくキーごと落とす
                  if (v) next[i] = { ...slot, access_code_prop: v };
                  else {
                    const { access_code_prop: _drop, ...rest } = slot;
                    next[i] = rest;
                  }
                  onChange(next);
                }}
              />
            </div>
          )}
          <button
            style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px", marginTop: 18 }}
            onClick={() => onChange(slots.filter((_, j) => j !== i))}
          >
            削除
          </button>
        </div>
      ))}
      <button
        style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
        onClick={() => onChange([...slots, { name_prop: "", email_prop: "" }])}
      >
        + {label}を追加
      </button>
    </>
  );
}

function ShareRecipientsForm({
  value,
  properties,
  onChange,
}: {
  value: {
    share_recipient_source: "property" | "manual";
    share_recipient_slots: Slot[];
  };
  properties: HubSpotProp[];
  onChange: (v: typeof value) => void;
}) {
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <div style={labelStyle}>共有先の取得方法</div>
        <select
          style={selectStyle}
          value={value.share_recipient_source}
          onChange={(e) =>
            onChange({ ...value, share_recipient_source: e.target.value as "property" | "manual" })
          }
        >
          {Object.entries(SHARE_SOURCE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {value.share_recipient_source === "property" && (
        <SlotEditor
          label="共有先プロパティ"
          hint="書類の進捗を共有する相手（署名権限なし）の氏名とメールアドレスが入っている HubSpot プロパティの組を指定します。"
          slots={value.share_recipient_slots}
          properties={properties}
          onChange={(next) => onChange({ ...value, share_recipient_slots: next })}
        />
      )}
    </>
  );
}

function RecipientsForm({
  value,
  properties,
  onChange,
}: {
  value: {
    recipient_source: "property" | "contact" | "manual";
    recipient_slots: Slot[];
    fixed_recipients: Array<{ name: string; email: string; organization?: string }>;
    first_signer_name_prop?: string;
    first_signer_email_prop?: string;
  };
  properties: HubSpotProp[];
  onChange: (v: typeof value) => void;
}) {
  const list = value.fixed_recipients;
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <div style={labelStyle}>宛先の取得方法</div>
        <select
          style={selectStyle}
          value={value.recipient_source}
          onChange={(e) => onChange({ ...value, recipient_source: e.target.value as any })}
        >
          {Object.entries(RECIPIENT_SOURCE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {value.recipient_source === "property" && (
        <div style={{ marginBottom: 16 }}>
          <SlotEditor
            label="宛先プロパティ"
            hint="署名者の氏名とメールアドレスが入っている HubSpot プロパティの組を指定します。上から順に署名順になります。アクセスコードを指定すると、その宛先は書類を開くときにコードの入力を求められます（コードの生成と通知は HubSpot 側で行います）。"
            slots={value.recipient_slots}
            properties={properties}
            showAccessCode
            onChange={(next) => onChange({ ...value, recipient_slots: next })}
          />
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={labelStyle}>1 人目の署名者（社内・任意）</div>
        <div style={{ fontSize: 12, color: "#6b6b68", marginBottom: 10, lineHeight: 1.7 }}>
          レコードのプロパティから読み取り、<b>署名順の先頭</b>に自動で追加します。
          多くの場合は営業担当です。<b>送信モーダルには表示されません。</b>
          氏名とメールアドレスの<b>両方</b>を選んだときだけ有効になります。
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#9a9a96", marginBottom: 4 }}>氏名</div>
            <PropSelect
              value={value.first_signer_name_prop ?? ""}
              properties={properties}
              onChange={(v) => onChange({ ...value, first_signer_name_prop: v })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#9a9a96", marginBottom: 4 }}>メールアドレス</div>
            <PropSelect
              value={value.first_signer_email_prop ?? ""}
              properties={properties}
              onChange={(v) => onChange({ ...value, first_signer_email_prop: v })}
            />
          </div>
        </div>
        {/*
          HubSpot の「ユーザー」型プロパティ（取引担当者など）は値が Owner ID なので、
          そのままではメールアドレスとして使えない。ここで案内しておかないと
          「選べたのに送信できない」になる（ops/PROSPECT_LOGOS_PLAN.md §3-2）
        */}
        <div style={{ marginTop: 10, padding: "10px 12px", background: "#FDF6E7", border: "1px solid #EFDFB8", borderRadius: 8, fontSize: 12, color: "#6b5a2e", lineHeight: 1.7 }}>
          メールアドレスは<b>メール型・テキスト型のプロパティ</b>を選んでください。
          HubSpot の「ユーザー」型プロパティ（取引担当者など）の値は Owner ID のため使えません。
          ワークフローで担当者のメールアドレスをテキストプロパティへコピーしてお使いください。
          <br />
          設定したプロパティが<b>空のレコードでは送信できません</b>（署名者が抜けた書類を出さないため）。
        </div>
      </div>

      <div>
        <div style={labelStyle}>固定送付宛先（送信モーダルの初期値）</div>
        {list.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              placeholder="氏名"
              value={r.name}
              onChange={(e) => {
                const next = [...list];
                next[i] = { ...r, name: e.target.value };
                onChange({ ...value, fixed_recipients: next });
              }}
            />
            <input
              style={{ ...inputStyle, flex: 1 }}
              placeholder="メールアドレス"
              value={r.email}
              onChange={(e) => {
                const next = [...list];
                next[i] = { ...r, email: e.target.value };
                onChange({ ...value, fixed_recipients: next });
              }}
            />
            <button
              style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
              onClick={() => onChange({ ...value, fixed_recipients: list.filter((_, j) => j !== i) })}
            >
              削除
            </button>
          </div>
        ))}
        <button
          style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
          onClick={() => onChange({ ...value, fixed_recipients: [...list, { name: "", email: "" }] })}
        >
          + 宛先を追加
        </button>

        <div style={{ marginTop: 10, padding: "12px 16px", background: "#FAEEDA", borderLeft: "3px solid #BA7517", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#854F0B", lineHeight: 1.8 }}>
          固定送付宛先は<b>送信モーダルに初期表示される値</b>です。
          送信時に削除できるため、必ず含まれることを保証するものではありません。
        </div>
      </div>
    </>
  );
}

/**
 * 送付ファイル。
 * プロパティ設定タブと同じ「送信元ファイルプロパティ」の考え方で、
 * プロパティ名の並びを編集し、内部表現の file_slots に変換する。
 */
function FilesForm({
  value,
  properties,
  onChange,
}: {
  value: { file_slots: Array<{ label: string; type: string; prop: string }>; overflow_mode: "attachment" | "reject" };
  properties: HubSpotProp[];
  onChange: (v: typeof value) => void;
}) {
  const props = value.file_slots.filter((s) => s.type === "property").map((s) => s.prop);
  /**
   * label には **HubSpot のプロパティラベル**（日本語）を入れる。
   * 送信モーダルの候補一覧・選択済み一覧はこの値を表示に使うので、
   * ここでプロパティ名を入れると内部名（`c_item_file_estimate` 等）が
   * 担当者の画面に並ぶ。ラベルが引けないときはプロパティ名で代替する。
   */
  const setProps = (next: string[]) =>
    onChange({
      ...value,
      file_slots: next.map((p) => ({
        label: properties.find((x) => x.name === p)?.label || p,
        type: "property",
        prop: p,
      })),
    });

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <div style={labelStyle}>送信元ファイルプロパティ</div>
        <div style={{ fontSize: 12, color: "#6b6b68", marginBottom: 10, lineHeight: 1.7 }}>
          送信モーダルで選択可能にするファイルプロパティを指定します。
          ここで設定したプロパティにファイルが格納されていれば、送信時に選択できます。
        </div>
        {props.map((prop, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <PropSelect
                value={prop}
                properties={properties}
                filterType="file"
                onChange={(v) => {
                  const next = [...props];
                  next[i] = v;
                  setProps(next);
                }}
              />
            </div>
            <button
              style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
              onClick={() => setProps(props.filter((_, j) => j !== i))}
            >
              削除
            </button>
          </div>
        ))}
        <button
          style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
          onClick={() => setProps([...props, ""])}
        >
          + ファイルプロパティを追加
        </button>
      </div>

      <div>
        <div style={labelStyle}>ファイル数が上限を超えたとき</div>
        <select
          style={selectStyle}
          value={value.overflow_mode}
          onChange={(e) => onChange({ ...value, overflow_mode: e.target.value as "attachment" | "reject" })}
        >
          <option value="attachment">超過分は添付ファイルとして送る</option>
          <option value="reject">超過分は送信しない</option>
        </select>
      </div>
    </>
  );
}

type TemplateValue = {
  default_template_id: string;
  template_source?: "fixed" | "property";
  template_prop?: string;
  template_map?: TemplateMapEntry[];
  stamp_prop?: string;
  stamp_map?: StampMapEntry[];
};

function TemplateForm({
  value,
  templates,
  templatesLoaded,
  properties,
  onChange,
}: {
  value: TemplateValue;
  templates: Array<{ id: string; title: string }>;
  templatesLoaded: boolean;
  properties: HubSpotProp[];
  onChange: (v: TemplateValue) => void;
}) {
  const source = value.template_source ?? "fixed";
  const map = value.template_map ?? [];

  /**
   * 判定に使うプロパティが選択式なら、その選択肢から選ばせる。
   *
   * **自由入力にしてはいけない。** レコードに入っているのは内部値であって
   * 画面のラベルではないため、ラベルを手入力すると永久に一致せず、
   * 「切替を設定したのに常に既定テンプレートになる」状態になる。
   */
  const selectedProp = properties.find((p) => p.name === value.template_prop);
  const propOptions = selectedProp?.options ?? [];
  const isEnum = propOptions.length > 0;

  /** テンプレートを1つ選ぶ欄。自由入力はさせない（存在しない ID を保存できてしまう） */
  const TemplateSelect = ({
    v,
    onSet,
    emptyLabel,
  }: {
    v: string;
    onSet: (next: string) => void;
    emptyLabel: string;
  }) => (
    <select style={{ ...selectStyle, flex: 1 }} value={v} onChange={(e) => onSet(e.target.value)}>
      <option value="">{emptyLabel}</option>
      {templates.map((t) => (
        <option key={t.id} value={t.id}>{t.title}</option>
      ))}
      {/* 保存済みの値が一覧に無い場合も選択状態を保てるようにする */}
      {v && !templates.some((t) => t.id === v) && (
        <option value={v}>（このクライアントIDに存在しません）{v}</option>
      )}
    </select>
  );

  if (!templatesLoaded) {
    return <div style={{ fontSize: 13, color: "#6b6b68" }}>テンプレート一覧を取得中...</div>;
  }

  return (
    <div>
      <div style={labelStyle}>テンプレートの決め方</div>
      <select
        style={selectStyle}
        value={source}
        onChange={(e) => onChange({ ...value, template_source: e.target.value as "fixed" | "property" })}
      >
        <option value="fixed">常に同じテンプレートを使う</option>
        <option value="property">プロパティの値で切り替える</option>
      </select>

      {source === "property" && (
        <div style={{ marginTop: 16 }}>
          <div style={labelStyle}>判定に使うプロパティ</div>
          <PropSelect
            value={value.template_prop ?? ""}
            properties={properties}
            // プロパティを変えたら、前のプロパティの値で作った対応表は使えない
            onChange={(v) => onChange({ ...value, template_prop: v, template_map: [] })}
          />
          <div style={{ fontSize: 12, color: "#6b6b68", marginTop: 6, lineHeight: 1.7 }}>
            書類種別などのプロパティを選び、値ごとに使うテンプレートを指定します。
          </div>

          {value.template_prop && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: "#9a9a96", marginBottom: 4 }}>
                値とテンプレートの対応
                {isEnum ? "" : "（レコードに入っている値をそのまま入力します）"}
              </div>
              {map.map((entry, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                  {isEnum ? (
                    <select
                      style={{ ...selectStyle, flex: 1 }}
                      value={entry.value}
                      onChange={(e) => {
                        const next = [...map];
                        next[i] = { ...entry, value: e.target.value };
                        onChange({ ...value, template_map: next });
                      }}
                    >
                      <option value="">-- 選択してください --</option>
                      {propOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                      {entry.value && !propOptions.some((o) => o.value === entry.value) && (
                        <option value={entry.value}>（選択肢にありません）{entry.value}</option>
                      )}
                    </select>
                  ) : (
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      placeholder="賃貸借契約"
                      value={entry.value}
                      onChange={(e) => {
                        const next = [...map];
                        next[i] = { ...entry, value: e.target.value };
                        onChange({ ...value, template_map: next });
                      }}
                    />
                  )}
                  <span style={{ fontSize: 12, color: "#9a9a96" }}>→</span>
                  <TemplateSelect
                    v={entry.template_id}
                    emptyLabel="-- テンプレートを選択 --"
                    onSet={(next) => {
                      const rows = [...map];
                      rows[i] = { ...entry, template_id: next };
                      onChange({ ...value, template_map: rows });
                    }}
                  />
                  <button
                    style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
                    onClick={() => onChange({ ...value, template_map: map.filter((_, j) => j !== i) })}
                  >
                    削除
                  </button>
                </div>
              ))}
              <button
                style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
                onClick={() => onChange({ ...value, template_map: [...map, { value: "", template_id: "" }] })}
              >
                + 対応を追加
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <div style={labelStyle}>
          {source === "property" ? "一致しなかったときのテンプレート" : "デフォルトのテンプレート"}
        </div>
        <div style={{ display: "flex" }}>
          <TemplateSelect
            v={value.default_template_id}
            emptyLabel="-- 使わない --"
            onSet={(v) => onChange({ ...value, default_template_id: v })}
          />
        </div>
        <div style={{ fontSize: 12, color: "#6b6b68", marginTop: 6, lineHeight: 1.7 }}>
          このクライアントIDのクラウドサインアカウントに登録されているテンプレートから選びます。
          {source === "property" && "対応表に無い値・空の値のときはこれが使われます（送信は止まりません）。"}
        </div>
      </div>

      {/*
        署名欄はテンプレート側のファイルに紐づくため、添付には付かない。
        「添付にも署名させたい」という誤解が最も起きやすいところなので、
        設定する時点で伝える（ops/PROSPECT_LOGOS_PLAN.md §13）
      */}
      <div style={{ marginTop: 14, padding: "12px 16px", background: "#FAEEDA", borderLeft: "3px solid #BA7517", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#854F0B", lineHeight: 1.8 }}>
        テンプレートの書類本体に加えて、送信モーダルで<b>添付ファイルを選べます</b>。
        ただし<b>添付には署名欄が付きません</b>（署名欄はテンプレートの書類本体に定義されたものだけです）。
      </div>

      <StampPositionsForm
        prop={value.stamp_prop ?? ""}
        map={value.stamp_map ?? []}
        properties={properties}
        onChange={(next) => onChange({ ...value, ...next })}
      />
    </div>
  );
}


/**
 * パイプライン由来のプロパティ名。
 *
 * HubSpot のプロパティ API はこれらに `options` を返さないため、
 * 選択肢は Pipelines API（`/api/properties/pipeline-stages`）から補う。
 * 取引は `dealstage` / `pipeline`、チケットとカスタムオブジェクトは `hs_pipeline_stage` / `hs_pipeline`。
 */
const STAGE_PROPS = ["dealstage", "hs_pipeline_stage"];
const PIPELINE_PROPS = ["pipeline", "hs_pipeline"];

/**
 * 送信できる条件（承認ガード）。
 *
 * レコードのプロパティの値を見て、送信を許すかどうかを決める。
 * 承認そのものは HubSpot のワークフロー／プロパティで表現してもらい、
 * ここではその結果を見るだけにしている。
 *
 * **プロパティ未選択 = 条件なし。** 既存テナントの挙動を変えないため、
 * 「条件を付けていない」が既定であることが画面から分かるようにする。
 */
function GateForm({
  value,
  properties,
  pipelines,
  pipelineStages,
  onChange,
}: {
  value: { gate_prop: string; gate_operator: "equals" | "in" | "not_empty"; gate_values: string[] };
  properties: HubSpotProp[];
  pipelines: Pipeline[];
  pipelineStages: PipelineStage[];
  onChange: (v: { gate_prop: string; gate_operator: "equals" | "in" | "not_empty"; gate_values: string[] }) => void;
}) {
  const values = value.gate_values ?? [];
  /**
   * 選ばれたプロパティが選択式なら、その選択肢から選ばせる。
   *
   * **自由入力にしてはいけない。** 選択式プロパティのレコード上の値は
   * 内部値（取引ステージなら「承認済み」ではなくステージ ID）なので、
   * 画面のラベルを手入力しても永久に一致せず、**全部ブロックされる。**
   */
  const selected = properties.find((p) => p.name === value.gate_prop);
  /**
   * 取引ステージ・パイプラインは `options` が空で返るので、Pipelines API の結果を選択肢にする。
   *
   * ステージのラベルはパイプラインをまたいで重複する（「失注」など）ため、
   * **パイプライン名を添えて表示する。** 添えないと、どちらの「失注」を選んだのか分からない。
   */
  const pipelineOptions: Array<{ value: string; label: string }> = STAGE_PROPS.includes(value.gate_prop)
    ? pipelineStages.map((s) => ({ value: s.stageId, label: `${s.pipelineLabel} / ${s.stageLabel}` }))
    : PIPELINE_PROPS.includes(value.gate_prop)
      ? pipelines.map((p) => ({ value: p.id, label: p.label }))
      : [];
  const options = pipelineOptions.length > 0 ? pipelineOptions : (selected?.options ?? []);
  const isEnum = options.length > 0;

  /** 値を1つ選ぶ／入力する欄。選択式かどうかで見た目を変える */
  const ValueField = ({ v, onSet }: { v: string; onSet: (next: string) => void }) =>
    isEnum ? (
      <select style={{ ...selectStyle, flex: 1 }} value={v} onChange={(e) => onSet(e.target.value)}>
        <option value="">-- 選択してください --</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        {/* 保存済みの値が選択肢に無い場合も選択状態を保つ（選択肢が削除されたとき） */}
        {v && !options.some((o) => o.value === v) && (
          <option value={v}>（選択肢にありません）{v}</option>
        )}
      </select>
    ) : (
      <input
        style={{ ...inputStyle, flex: 1 }}
        placeholder="承認済み"
        value={v}
        onChange={(e) => onSet(e.target.value)}
      />
    );

  return (
    <div>
      <div style={labelStyle}>判定に使うプロパティ</div>
      <PropSelect
        value={value.gate_prop}
        properties={properties}
        // プロパティを変えたら、前のプロパティの選択肢の値が残らないよう捨てる
        onChange={(v) => onChange({ ...value, gate_prop: v, gate_values: [] })}
      />
      <div style={{ fontSize: 12, color: "#6b6b68", marginTop: 6, lineHeight: 1.7 }}>
        未選択のときは条件なし（いつでも送信できます）。承認ステータスなど、
        HubSpot のワークフローで更新するプロパティを選びます。
      </div>

      {value.gate_prop && (
        <>
          <div style={{ ...labelStyle, marginTop: 16 }}>送信できる条件</div>
          <select
            style={selectStyle}
            value={value.gate_operator}
            onChange={(e) =>
              onChange({ ...value, gate_operator: e.target.value as typeof value.gate_operator })
            }
          >
            <option value="not_empty">値が入っていれば送信できる</option>
            <option value="equals">値が一致すれば送信できる</option>
            <option value="in">いずれかの値に一致すれば送信できる</option>
          </select>

          {value.gate_operator !== "not_empty" && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: "#9a9a96", marginBottom: 4 }}>
                {value.gate_operator === "equals" ? "送信できる値" : "送信できる値（複数）"}
                {isEnum ? "" : "（レコードに入っている値をそのまま入力します）"}
              </div>
              {/*
                パイプライン由来のプロパティなのに一覧が取れなかったとき。
                この状態で画面のラベル（「承認済み」等）を入力するとステージ ID と
                永久に一致せず、**全レコードが送信不可になる。** 先に警告する。
              */}
              {!isEnum && (STAGE_PROPS.includes(value.gate_prop) || PIPELINE_PROPS.includes(value.gate_prop)) && (
                <div style={{ marginBottom: 8, padding: "8px 10px", background: "#FCEBEB", border: "1px solid #F0C9C9", borderRadius: 8, fontSize: 12, color: "#A32D2D", lineHeight: 1.7 }}>
                  パイプラインの一覧を取得できませんでした。画面に見えるステージ名を入力しても一致しません
                  （レコードにはステージ ID が入っています）。画面を開き直すか、別のプロパティで条件を書いてください。
                </div>
              )}
              {(values.length > 0 ? values : [""]).map((v, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <ValueField
                    v={v}
                    onSet={(nextValue) => {
                      const next = values.length > 0 ? [...values] : [""];
                      next[i] = nextValue;
                      onChange({ ...value, gate_values: next });
                    }}
                  />
                  {value.gate_operator === "in" && (
                    <button
                      style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
                      onClick={() => onChange({ ...value, gate_values: values.filter((_, j) => j !== i) })}
                    >
                      削除
                    </button>
                  )}
                </div>
              ))}
              {value.gate_operator === "in" && (
                <button
                  style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
                  onClick={() => onChange({ ...value, gate_values: [...values, ""] })}
                >
                  + 値を追加
                </button>
              )}
            </div>
          )}

          <div style={{ marginTop: 14, padding: "10px 12px", background: "#FDF6E7", border: "1px solid #EFDFB8", borderRadius: 8, fontSize: 12, color: "#6b5a2e", lineHeight: 1.7 }}>
            条件を満たさないレコードでは、カードの送信ボタンが押せなくなり、送信も拒否されます。
            HubSpot のレコードを読み取れなかった場合も送信しません。
          </div>
        </>
      )}
    </div>
  );
}

/**
 * 結果の格納先。クラウドサインの結果をどの HubSpot プロパティに書くか。
 *
 * デフォルト側は「プロパティ」タブが所有しているため、この画面には
 * クライアントIDごとの上書きとしてのみ現れる。
 */
function StorageForm({
  value,
  properties,
  onChange,
}: {
  value: StorageOverrideValue;
  properties: HubSpotProp[];
  onChange: (v: StorageOverrideValue) => void;
}) {
  const set = <K extends keyof StorageOverrideValue>(k: K, v: StorageOverrideValue[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <>
      <div style={{ fontSize: 12, color: "#6b6b68", marginBottom: 14, lineHeight: 1.7 }}>
        このクライアントIDで送った書類の結果を書き込むプロパティです。
        部署ごとに分けたい場合に設定します。
      </div>

      {!value.prop_status && (
        <div style={{ marginBottom: 14, padding: "12px 16px", background: "#FAEEDA", borderLeft: "3px solid #BA7517", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#854F0B", lineHeight: 1.8 }}>
          <b>クラウドサインステータスが未設定です。</b>
          <br />
          この項目が空だと、このオブジェクトタイプでは<b>ステータス同期が一切行われません</b>
          （締結完了URL・締結完了ファイル・合意締結証明書も書き込まれません）。
        </div>
      )}

      {([
        { k: "prop_status" as const, label: "クラウドサインステータス" },
        { k: "prop_document_id" as const, label: "クラウドサイン書類ID" },
        { k: "prop_draft_url" as const, label: "下書きURL" },
        { k: "prop_completed_url" as const, label: "締結完了URL" },
      ]).map((f) => (
        <div key={f.k} style={{ marginBottom: 12 }}>
          <div style={labelStyle}>{f.label}</div>
          <PropSelect value={value[f.k]} properties={properties} onChange={(v) => set(f.k, v)} />
        </div>
      ))}

      <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid #e8e7e3" }}>
        <div style={labelStyle}>締結完了ファイルの処理方式</div>
        <select
          style={selectStyle}
          value={value.completed_url_mode}
          onChange={(e) => set("completed_url_mode", e.target.value as "url_only" | "reupload")}
        >
          <option value="url_only">URLのみ保存</option>
          <option value="reupload">HubSpotへ再アップロード</option>
        </select>
        {value.completed_url_mode === "reupload" && (
          <div style={{ marginTop: 12 }}>
            <div style={labelStyle}>再アップロード先</div>
            <PropSelect
              value={value.reupload_prop}
              properties={properties}
              filterType="file"
              onChange={(v) => set("reupload_prop", v)}
            />
            <WriteMode
              value={value.completed_file_write_mode}
              onChange={(v) => set("completed_file_write_mode", v)}
            />
          </div>
        )}
      </div>

      <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid #e8e7e3" }}>
        <div style={labelStyle}>合意締結証明書の処理方式</div>
        <select
          style={selectStyle}
          value={value.agreement_url_mode}
          onChange={(e) => set("agreement_url_mode", e.target.value as "none" | "url_only" | "reupload")}
        >
          <option value="none">格納しない</option>
          <option value="url_only">URLのみ保存</option>
          <option value="reupload">HubSpotへ再アップロード</option>
        </select>
        {value.agreement_url_mode === "reupload" && (
          <div style={{ marginTop: 12 }}>
            <div style={labelStyle}>再アップロード先</div>
            <PropSelect
              value={value.agreement_reupload_prop}
              properties={properties}
              filterType="file"
              onChange={(v) => set("agreement_reupload_prop", v)}
            />
            <WriteMode
              value={value.agreement_file_write_mode}
              onChange={(v) => set("agreement_file_write_mode", v)}
            />
          </div>
        )}
      </div>
    </>
  );
}

/** ファイルプロパティに既存値があるときの扱い */
function WriteMode({
  value,
  onChange,
}: {
  value: "overwrite" | "append";
  onChange: (v: "overwrite" | "append") => void;
}) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, color: "#6b6b68", marginBottom: 6 }}>既にファイルが入っているとき</div>
      <div style={{ display: "flex", border: "1px solid #e8e7e3", borderRadius: 8, overflow: "hidden", width: "fit-content" }}>
        {([
          { v: "overwrite" as const, label: "置き換える" },
          { v: "append" as const, label: "追記する" },
        ]).map((opt) => (
          <button
            key={opt.v}
            onClick={() => onChange(opt.v)}
            style={{
              padding: "6px 14px", fontSize: 12, border: "none", cursor: "pointer",
              background: value === opt.v ? "#534AB7" : "white",
              color: value === opt.v ? "white" : "#6b6b68",
              fontWeight: value === opt.v ? 600 : 400,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {value === "append" && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#854F0B", lineHeight: 1.7 }}>
          HubSpot の仕様により 1 プロパティ最大 10 件です。10 件に達すると以降は格納されません。
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── オブジェクトタイプのブロック ───────────────────────────

function ObjectTypeBlock({
  clientIdId,
  objectType,
  objectLabel,
  templates,
  templatesLoaded,
  defaultExpanded,
  onSaved,
}: {
  /** null = デフォルト送信設定の編集（Tenant.settings.object_mappings） */
  clientIdId: string | null;
  objectType: string;
  objectLabel: string;
  templates: Array<{ id: string; title: string }>;
  templatesLoaded: boolean;
  defaultExpanded: boolean;
  onSaved: () => void;
}) {
  const isDefaultMode = clientIdId === null;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [applied, setApplied] = useState<ClientSendAppliedState | null>(null);
  const [state, setState] = useState<BlockState | null>(null);
  const [properties, setProperties] = useState<HubSpotProp[]>([]);
  /**
   * 取引ステージ・パイプラインの選択肢。
   * これらは /properties/list の options に含まれない（HubSpot の仕様）ため別途取得する。
   */
  const [pipelineStages, setPipelineStages] = useState<PipelineStage[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    if (clientIdId === null) {
      // デフォルト送信設定は object_mappings をそのまま読む。継承元は無い
      api.getSettings("object_mappings")
        .then((d) => {
          const m = (d.object_mappings || {})[objectType] ?? {};
          setState(defaultBlockStateFromMapping(m));
          // 継承表示に使う defaults は不要だが、型を満たすため自分自身を入れる
          setApplied(null);
        })
        .catch((e) => setError(e.message));
    } else {
      api.getClientSendResolved(clientIdId, objectType)
        .then((a) => { setApplied(a); setState(initialBlockState(a)); })
        .catch((e) => setError(e.message));
    }
    // 送付ファイルのプロパティ選択に使う
    api.getPropertyList(objectType)
      .then((r: any) => setProperties(r.properties || []))
      .catch(() => setProperties([]));
    // 送信できる条件で取引ステージを選べるようにする
    api.getPipelineStages(objectType)
      .then((r) => { setPipelineStages(r.stages || []); setPipelines(r.pipelines || []); })
      .catch(() => { setPipelineStages([]); setPipelines([]); });
  }, [clientIdId, objectType]);

  const overridden = applied?.resolved.overriddenSections ?? [];

  /**
   * デフォルト送信設定の保存。
   * 直前に現在値を読み直し、送信側が所有するフィールドだけを差し替える。
   * プロパティタブが同じキーに格納先プロパティを持っているため、
   * 全体を書き戻すと相手の変更を巻き戻す（objectMappingFieldOwnership.ts 参照）。
   */
  const saveDefault = async (s: BlockState) => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const current = await api.getSettings("object_mappings").catch(() => ({ object_mappings: {} }));
      const all = current.object_mappings || {};
      const edited = buildDefaultMappingFields(s);
      // source_file_props は file_slots からの派生。正準は file_slots
      edited.source_file_props = deriveSourceFileProps(edited.file_slots);
      // この画面が object_mappings の唯一の書き手（タブ統合後）
      all[objectType] = mergeOwnedFields(all[objectType] ?? {}, edited, "all");
      await api.updateSettings("object_mappings", all);
      setState(defaultBlockStateFromMapping(all[objectType]));
      setSaved(true);
      onSaved();
    } catch (e: any) {
      setError(`保存に失敗しました（${e.message}）。設定は変更されていません。もう一度お試しください。`);
    } finally {
      setSaving(false);
    }
  };

  const save = async (payload: ReturnType<typeof buildSavePayload>) => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      // 1リクエストで原子的に適用される。部分保存は起きない
      const res = await api.saveClientSendOverrides({ clientIdId: clientIdId as string, objectType, sections: payload });
      // サーバーが返した適用後の状態で描き直す（楽観的更新をしない）
      setApplied(res);
      setState(initialBlockState(res));
      setSaved(true);
      onSaved();
    } catch (e: any) {
      // 原子的なので、失敗したときは何も保存されていない
      setError(`保存に失敗しました（${e.message}）。設定は変更されていません。もう一度お試しください。`);
    } finally {
      setSaving(false);
    }
  };

  // ここも overflow: hidden 不可（中のドロップダウンが切られる）
  return (
    <div style={{ border: "1px solid #e8e7e3", borderRadius: 10, marginBottom: 12, background: "white" }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 12,
          padding: "14px 18px", background: "white", border: "none", cursor: "pointer", textAlign: "left",
          borderRadius: expanded ? "10px 10px 0 0" : 10,
        }}
      >
        <span style={{ fontSize: 13, color: "#6b6b68", width: 14 }}>{expanded ? "▾" : "▸"}</span>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{objectLabel}</span>
        {!isDefaultMode && (overridden.length > 0
          ? <Badge bg="#E1F5EE" color="#0F6E56">個別 {overridden.length} / {OVERRIDE_SECTIONS.length}</Badge>
          : <Badge bg="#F0EFEB" color="#6b6b68">すべてデフォルト</Badge>)}
        <span style={{ flex: 1, fontSize: 13, color: "#6b6b68" }}>
          {isDefaultMode ? (state ? summarizeDefaultBlock(state) : "") : summarizeBlock(overridden)}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: "0 18px 18px" }}>
          {error && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}
          {!state || (!isDefaultMode && !applied) ? (
            <div style={{ padding: 20, color: "#6b6b68", fontSize: 14 }}>読み込み中...</div>
          ) : (
            <>
              {(isDefaultMode ? DEFAULT_SECTIONS : OVERRIDE_SECTIONS).map((s) => (
                <SectionBox
                  key={s}
                  section={s}
                  state={state[s]}
                  // デフォルト側では継承が無いため参照されない
                  defaults={applied?.defaults ?? EMPTY_DEFAULTS}
                  storageDefaults={applied?.storageDefaults ?? EMPTY_STORAGE_DEFAULTS}
                  templates={templates}
                  templatesLoaded={templatesLoaded}
                  properties={properties}
                  pipelines={pipelines}
                  pipelineStages={pipelineStages}
                  allowInherit={!isDefaultMode}
                  onChange={(next) => { setState({ ...state, [s]: next }); setSaved(false); }}
                />
              ))}

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                <button
                  style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
                  disabled={saving}
                  onClick={() => (isDefaultMode ? saveDefault(state) : save(buildSavePayload(state)))}
                >
                  {saving ? "保存中..." : `${objectLabel}の設定を保存`}
                </button>
                {saved && <span style={{ fontSize: 14, color: "#0F6E56", fontWeight: 600 }}>保存しました</span>}
                <div style={{ flex: 1 }} />
                {/* 削除はクライアントID側だけ。デフォルト側で同じキーを消すと
                    プロパティタブが持つ格納先プロパティまで消える */}
                {!isDefaultMode && (
                  <button
                    style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px", color: "#A32D2D", borderColor: "#f0c8c8" }}
                    disabled={saving || overridden.length === 0}
                    onClick={() => {
                      if (!confirm(`${objectLabel}の個別設定をすべて削除し、デフォルト設定に戻しますか？`)) return;
                      save(buildClearPayload());
                    }}
                  >
                    このオブジェクトの個別設定を削除
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────── 本体 ───────────────────────────────

export default function ObjectOverrideBlocks({ clientIdId }: { clientIdId: string | null }) {
  const isDefaultMode = clientIdId === null;
  const [objectOptions, setObjectOptions] = useState<ObjectOption[]>([]);
  const [addedTypes, setAddedTypes] = useState<string[]>([]);
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [pickType, setPickType] = useState("");
  const [templates, setTemplates] = useState<Array<{ id: string; title: string }>>([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const objectLabel = (key: string) => objectOptions.find((o) => o.key === key)?.label ?? key;

  useEffect(() => {
    setTemplatesLoaded(false);
    setTemplates([]);
    // デフォルト側は既定のクライアントIDのテンプレート一覧を使う
    api.getCloudSignTemplates(clientIdId ?? undefined)
      .then((r) => setTemplates(r.templates || []))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoaded(true));

    const existing = clientIdId === null
      // デフォルト側は既に設定済みのオブジェクトタイプを必ず全部出す。
      // 追加型のまま 0 件で表示すると、既存テナントには設定が消えたように見える
      ? api.getSettings("object_mappings").then((d) => Object.keys(d.object_mappings || {}))
      : api.getClientSendOverrides(clientIdId).then((ov) =>
          groupByObjectType(ov.overrides).map((g) => g.objectType),
        );

    Promise.all([api.getObjectSchemas(), existing])
      .then(([schemas, types]) => {
        setObjectOptions([
          ...schemas.standard.map((s) => ({ key: s.key, label: s.label })),
          ...schemas.custom.map((s) => ({ key: s.key, label: `${s.label}（カスタム）` })),
        ]);
        setAddedTypes(types);
      })
      .catch((e) => setError(e.message));

    setJustAdded(null);
    setPickType("");
  }, [clientIdId]);

  const remaining = objectOptions.filter((o) => !addedTypes.includes(o.key));

  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
        誰に・何を送るか（オブジェクトタイプごと）
      </div>
      <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 6, lineHeight: 1.7 }}>
        送付宛先・送付ファイル・クラウドサインテンプレート・共有先（CC）
      </div>
      {isDefaultMode ? (
        <div style={{ fontSize: 12, color: "#6b6b68", marginBottom: 18, lineHeight: 1.7 }}>
          ここで設定した内容が、すべてのクライアントIDの既定値になります。
          クライアントIDごとに変えたい場合は、上のクライアントID一覧から個別に設定してください。
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "#185FA5", marginBottom: 18 }}>
          ※ この設定は上の「デフォルト / 個別」の選択とは独立です
        </div>
      )}

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "14px 16px", border: "1px dashed #d0cfc9", borderRadius: 10,
        background: "#fbfaf8", marginBottom: 16,
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>
            {isDefaultMode ? "オブジェクトタイプを追加" : "上書きするオブジェクトタイプを追加"}
          </div>
          <div style={{ fontSize: 12, color: "#6b6b68" }}>
            {isDefaultMode
              ? "設定済みのオブジェクトタイプは下に表示されています。ここでは未設定のものを追加できます。"
              : "追加したものだけが下に並びます。何も追加しなければ、すべてデフォルト設定のままです。"}
          </div>
        </div>
        <select style={{ ...selectStyle, width: 200 }} value={pickType} onChange={(e) => setPickType(e.target.value)}>
          <option value="">選択してください</option>
          {remaining.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <button
          style={{ ...btnPrimary, fontSize: 13, opacity: pickType ? 1 : 0.5 }}
          disabled={!pickType}
          onClick={() => {
            setAddedTypes([...addedTypes, pickType]);
            setJustAdded(pickType); // 追加した直後に設定するのが自然なので自動展開する
            setPickType("");
          }}
        >
          + 追加
        </button>
      </div>

      {addedTypes.length === 0 ? (
        <div style={{ padding: "24px 0", textAlign: "center", color: "#6b6b68", fontSize: 14 }}>
          {isDefaultMode
            ? "まだ設定がありません。上からオブジェクトタイプを追加してください。"
            : "個別設定はありません。すべてデフォルト設定を使います。"}
        </div>
      ) : (
        addedTypes.map((t) => (
          <ObjectTypeBlock
            key={t}
            clientIdId={clientIdId}
            objectType={t}
            objectLabel={objectLabel(t)}
            templates={templates}
            templatesLoaded={templatesLoaded}
            defaultExpanded={t === justAdded}
            onSaved={() => {}}
          />
        ))
      )}
    </div>
  );
}
